import { Dialog as DialogPrimitive } from 'radix-ui';
import * as React from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { XIcon } from 'lucide-react';

const Dialog =
  ({
    ...props
  }: React.ComponentProps<typeof DialogPrimitive.Root>) => {
    return <DialogPrimitive.Root data-slot='dialog' {...props} />;
  };

const DialogTrigger =
  ({
    ...props
  }: React.ComponentProps<typeof DialogPrimitive.Trigger>) => {
    return <DialogPrimitive.Trigger data-slot='dialog-trigger' {...props} />;
  };

const DialogPortal =
  ({
    ...props
  }: React.ComponentProps<typeof DialogPrimitive.Portal>) => {
    return <DialogPrimitive.Portal data-slot='dialog-portal' {...props} />;
  };

const DialogClose =
  ({
    ...props
  }: React.ComponentProps<typeof DialogPrimitive.Close>) => {
    return <DialogPrimitive.Close data-slot='dialog-close' {...props} />;
  };

const DialogOverlay =
  ({
    className,
    ...props
  }: React.ComponentProps<typeof DialogPrimitive.Overlay>) => {
    return (
      <DialogPrimitive.Overlay
        data-slot='dialog-overlay'
        className={cn(
          'tw:fixed',
          'tw:inset-0',
          'tw:isolate',
          'tw:z-50',
          'tw:bg-black/80', // 10->80
          'tw:duration-100',
          // 'tw:supports-backdrop-filter:backdrop-blur-xs',
          'tw:data-open:animate-in',
          'tw:data-open:fade-in-0',
          'tw:data-closed:animate-out',
          'tw:data-closed:fade-out-0',
          className
        )}
        {...props}
      />
    );
  };

const DialogContent =
  ({
    className,
    children,
    showCloseButton = true,
    hideOverlay = false,
    onInteractOutside,
    ...props
  }: React.ComponentProps<typeof DialogPrimitive.Content> & {
    showCloseButton?: boolean;
    hideOverlay?: boolean;
  }) => {
    return (
      <DialogPortal>
        {!hideOverlay && <DialogOverlay />}
        <DialogPrimitive.Content
          data-slot='dialog-content'
          onInteractOutside={(event) => {
            // When two dialogs are stacked (a dialog opened on top of another one),
            // clicking a button that closes the top dialog (e.g. Cancel or X) is
            // reported to the dialog below as an "outside interaction" after the top
            // dialog has already unmounted, which would close the bottom dialog too.
            // Ignore interactions originating inside any dialog content so only
            // genuine outside clicks (e.g. on the overlay) can dismiss this dialog.
            const target = event.target;
            if (target instanceof Element && target.closest('[data-slot="dialog-content"]')) {
              event.preventDefault();
              return;
            }
            onInteractOutside?.(event);
          }}
          className={cn(
            'tw:fixed',
            'tw:top-1/2',
            'tw:left-1/2',
            'tw:z-50',
            'tw:grid',
            'tw:w-full',
            'tw:max-w-[calc(100%-2rem)]',
            'tw:-translate-x-1/2',
            'tw:-translate-y-1/2',
            'tw:gap-6',
            'tw:rounded-xl',
            'tw:bg-popover',
            'tw:p-6',
            'tw:text-sm',
            'tw:text-popover-foreground',
            'tw:ring-1',
            'tw:ring-foreground/10',
            'tw:duration-100',
            'tw:outline-none',
            'tw:sm:max-w-md',
            'tw:data-open:animate-in',
            'tw:data-open:fade-in-0',
            'tw:data-open:zoom-in-95',
            'tw:data-closed:animate-out',
            'tw:data-closed:fade-out-0',
            'tw:data-closed:zoom-out-95',
            className
          )}
          {...props}
        >
          {children}
          {showCloseButton && (
            <DialogPrimitive.Close data-slot='dialog-close' asChild>
              <Button
                variant='ghost'
                className='tw:absolute tw:top-4 tw:right-4'
                size='icon-sm'
              >
                <XIcon
                />
                <span className='tw:sr-only'>Close</span>
              </Button>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    );
  };

const DialogHeader =
  ({ className, ...props }: React.ComponentProps<'div'>) => {
    return (
      <div
        data-slot='dialog-header'
        className={cn('tw:flex tw:flex-col tw:gap-2', className)}
        {...props}
      />
    );
  };

const DialogFooter =
  ({
    className,
    showCloseButton = false,
    children,
    ...props
  }: React.ComponentProps<'div'> & {
    showCloseButton?: boolean;
  }) => {
    return (
      <div
        data-slot='dialog-footer'
        className={cn(
          'tw:flex tw:flex-col-reverse tw:gap-2 tw:sm:flex-row tw:sm:justify-end',
          className
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close asChild>
            <Button variant='outline'>Close</Button>
          </DialogPrimitive.Close>
        )}
      </div>
    );
  };

const DialogTitle =
  ({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) => {
    return (
      <DialogPrimitive.Title
        data-slot='dialog-title'
        className={cn('tw:font-heading tw:leading-none tw:font-medium', className)}
        {...props}
      />
    );
  };

const DialogDescription =
  ({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Description>) => {
    return (
      <DialogPrimitive.Description
        data-slot='dialog-description'
        className={cn(
          'tw:text-sm tw:text-muted-foreground tw:*:[a]:underline tw:*:[a]:underline-offset-3 tw:*:[a]:hover:text-foreground',
          className
        )}
        {...props}
      />
    );
  };

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};

